export interface FileDownloadResult {
  file: File;
  originalUrl: string;
}

// Types de fichiers supportés
export const SUPPORTED_IMAGE_EXTENSIONS = [
  '.jpeg',
  '.jpg',
  '.png',
  '.webp',
  '.gif',
];
export const SUPPORTED_PDF_EXTENSIONS = ['.pdf'];

export const isPDFUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();
    return (
      pathname.endsWith('.pdf') || urlObj.searchParams.get('format') === 'pdf'
    );
  } catch {
    return false;
  }
};

export const isImageUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    // Vérifier l'extension dans le pathname
    const hasImageExtension = SUPPORTED_IMAGE_EXTENSIONS.some((ext) =>
      pathname.endsWith(ext)
    );

    // Vérifier le paramètre format dans l'URL
    const formatParam = urlObj.searchParams.get('format')?.toLowerCase();
    const hasImageFormat =
      formatParam &&
      ['jpeg', 'jpg', 'png', 'webp', 'gif'].includes(formatParam);

    return hasImageExtension || hasImageFormat || false;
  } catch {
    return false;
  }
};

export const isDataUrl = (url: string): boolean => {
  return url.startsWith('data:');
};

export const isImageDataUrl = (url: string): boolean => {
  if (!isDataUrl(url)) return false;

  const imageMimeTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
  ];
  return imageMimeTypes.some((mimeType) => url.startsWith(`data:${mimeType}`));
};

export const isSupportedFileUrl = (url: string): boolean => {
  return isPDFUrl(url) || isImageUrl(url) || isImageDataUrl(url);
};

export const extractUrlsFromDataTransfer = (
  dataTransfer: DataTransfer
): string[] => {
  const urls: string[] = [];

  // Gérer les data URLs d'images (drag & drop d'images)

  if (dataTransfer.types.includes('text/uri-list')) {
    const uriList = dataTransfer.getData('text/uri-list');

    if (uriList) {
      urls.push(...uriList.split('\n').filter((url) => url.trim()));
    }
  }

  // Gérer les URLs HTTP/HTTPS dans le texte
  if (dataTransfer.types.includes('text/plain')) {
    const text = dataTransfer.getData('text/plain');
    if (text) {
      const urlRegex = /https?:\/\/[^\s]+/g;
      const matches = text.match(urlRegex);
      if (matches) {
        urls.push(...matches);
      }
    }
  }

  return Array.from(new Set(urls.filter((url) => isSupportedFileUrl(url))));
};

const getContentTypeForUrl = (url: string): string => {
  if (isPDFUrl(url)) {
    return 'application/pdf';
  }

  if (isImageUrl(url)) {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    if (pathname.endsWith('.jpeg') || pathname.endsWith('.jpg')) {
      return 'image/jpeg';
    } else if (pathname.endsWith('.png')) {
      return 'image/png';
    } else if (pathname.endsWith('.webp')) {
      return 'image/webp';
    } else if (pathname.endsWith('.gif')) {
      return 'image/gif';
    }
  }

  return 'application/octet-stream';
};

const getFileTypeForUrl = (url: string): string => {
  if (isPDFUrl(url)) {
    return 'application/pdf';
  }

  if (isImageUrl(url)) {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    if (pathname.endsWith('.jpeg') || pathname.endsWith('.jpg')) {
      return 'image/jpeg';
    } else if (pathname.endsWith('.png')) {
      return 'image/png';
    } else if (pathname.endsWith('.webp')) {
      return 'image/webp';
    } else if (pathname.endsWith('.gif')) {
      return 'image/gif';
    }
  }

  return 'application/octet-stream';
};

export const downloadFileFromUrl = async (
  url: string
): Promise<FileDownloadResult> => {
  try {
    // Gérer les data URLs (images drag & drop)
    if (isImageDataUrl(url)) {
      const file = dataUrlToFile(url);
      return {
        file,
        originalUrl: url,
      };
    }

    // Gérer les URLs HTTP/HTTPS
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: '*/*',
      },
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    const expectedContentType = getContentTypeForUrl(url);

    // Vérifier que le type de contenu correspond au type attendu
    if (
      contentType &&
      !contentType.includes(expectedContentType.split('/')[0])
    ) {
      throw new Error(
        `Le contenu téléchargé n'est pas du type attendu. Attendu: ${expectedContentType}, Reçu: ${contentType}`
      );
    }

    const blob = await response.blob();
    const filename = extractFilenameFromUrl(url) || getDefaultFilename(url);

    const file = new File([blob], filename, {
      type: getFileTypeForUrl(url),
      lastModified: Date.now(),
    });

    return {
      file,
      originalUrl: url,
    };
  } catch (error) {
    throw new Error(
      `Erreur lors du téléchargement du fichier depuis ${url}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    );
  }
};

// Fonction de compatibilité pour maintenir l'API existante
export const downloadPDFFromUrl = async (
  url: string
): Promise<FileDownloadResult> => {
  if (!isPDFUrl(url)) {
    throw new Error("L'URL fournie n'est pas une URL de PDF valide");
  }
  return downloadFileFromUrl(url);
};

const extractFilenameFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop();

    if (filename && filename.includes('.')) {
      return decodeURIComponent(filename);
    }

    return null;
  } catch {
    return null;
  }
};

const dataUrlToFile = (dataUrl: string): File => {
  const [header, base64Data] = dataUrl.split(',');
  const mimeType =
    header.match(/data:([^;]+)/)?.[1] || 'application/octet-stream';

  // Décoder les données base64
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Créer le nom de fichier basé sur le type MIME
  const extension = mimeType.split('/')[1] || 'bin';
  const filename = `image.${extension}`;

  return new File([bytes], filename, {
    type: mimeType,
    lastModified: Date.now(),
  });
};

const getDefaultFilename = (url: string): string => {
  if (isPDFUrl(url)) {
    return 'document.pdf';
  }

  if (isImageUrl(url)) {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    if (pathname.endsWith('.jpeg') || pathname.endsWith('.jpg')) {
      return 'image.jpg';
    } else if (pathname.endsWith('.png')) {
      return 'image.png';
    } else if (pathname.endsWith('.webp')) {
      return 'image.webp';
    } else if (pathname.endsWith('.gif')) {
      return 'image.gif';
    }
  }

  if (isImageDataUrl(url)) {
    const mimeType = url.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
    const extension = mimeType.split('/')[1] || 'jpg';
    return `image.${extension}`;
  }

  return 'file';
};

export const downloadMultipleFiles = async (
  urls: string[]
): Promise<FileDownloadResult[]> => {
  const results: FileDownloadResult[] = [];
  const errors: string[] = [];

  for (const url of urls) {
    try {
      const result = await downloadFileFromUrl(url);
      results.push(result);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : 'Erreur inconnue');
    }
  }

  if (errors.length > 0 && results.length === 0) {
    throw new Error(
      `Aucun fichier n'a pu être téléchargé. Erreurs: ${errors.join(', ')}`
    );
  }

  return results;
};

// Fonction de compatibilité pour maintenir l'API existante
export const downloadMultiplePDFs = async (
  urls: string[]
): Promise<FileDownloadResult[]> => {
  const pdfUrls = urls.filter((url) => isPDFUrl(url));
  if (pdfUrls.length !== urls.length) {
    throw new Error(
      'Toutes les URLs fournies ne sont pas des URLs de PDF valides'
    );
  }
  return downloadMultipleFiles(pdfUrls);
};
